import type { ReactNode } from 'react';
import { SISTEMA } from '../../copy/index.js';
import { Boton } from './Boton.js';
import { Check, ICONO, WifiOff } from './iconos.js';

/**
 * Los estados transversales del sistema · S-01 a S-13.
 *
 * `dev/definition-of-done.md` exige carga, vacío, error y éxito en cada
 * componente. Están acá y no en cada pantalla para que el paciente no vea tres
 * lenguajes distintos para la misma situación — y porque el tono de estas
 * pantallas es una decisión de producto, no de quien escribe el componente.
 *
 * El tono lo fija `wireframes-sistema.html` y vale la pena decirlo entero: **el
 * error es del sistema, nunca del usuario**. Sin cruces rojas gigantes, sin
 * "loading…" sin contexto, sin checks verdes celebrando que se guardó una
 * preferencia. Un adulto mayor recién operado que ve una pantalla de alarma no
 * piensa "el servidor falló": piensa que rompió algo.
 */

// ─────────────────────────────────────────────────────────────────────────────
// S-04 · S-13 · Carga
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Skeleton · la silueta de lo que viene.
 *
 * Se prefiere al spinner porque anticipa la estructura: el usuario ya sabe que
 * viene una lista de pacientes o un home con dos cards, no una pantalla
 * genérica. `aria-hidden` porque para un lector de pantalla la silueta no dice
 * nada — lo que se anuncia es el `role="status"` del contenedor.
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <span aria-hidden="true" className={`block animate-pulse rounded-md bg-track ${className}`} />
  );
}

/** S-04 · el home del paciente mientras carga. */
export function SkeletonDeHome() {
  return (
    <div aria-busy="true" className="flex flex-col gap-5" role="status">
      <span className="sr-only">{SISTEMA.cargando.home}</span>
      <div className="flex items-center gap-4">
        <Skeleton className="size-14 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </div>
      <Skeleton className="h-24 w-full rounded-lg" />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-24 rounded-lg" />
      </div>
      <Skeleton className="h-16 w-full rounded-lg" />
      <Skeleton className="h-16 w-full rounded-lg" />
    </div>
  );
}

/** S-13 · una lista o tabla del workspace mientras carga. */
export function SkeletonDeLista({ filas = 5 }: { filas?: number }) {
  return (
    <div aria-busy="true" className="flex flex-col gap-3" role="status">
      <span className="sr-only">{SISTEMA.cargando.lista}</span>
      {Array.from({ length: filas }, (_, indice) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: son siluetas sin identidad
        <div className="flex items-center gap-4 rounded-lg bg-card p-4" key={indice}>
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

/**
 * El spinner, para esperas cortas dentro de una pantalla que ya se dibujó.
 *
 * Sigue existiendo porque no todo es una pantalla entera: un modal que confirma
 * o un tab que recarga no merecen una silueta completa.
 */
export function Cargando({ mensaje = SISTEMA.cargando.generico }: { mensaje?: string }) {
  return (
    <div aria-live="polite" className="flex flex-col items-center gap-4 py-12" role="status">
      <span
        aria-hidden="true"
        className="size-10 animate-spin rounded-full border-4 border-brand-soft border-t-navy"
      />
      <p className="text-lg text-muted-foreground">{mensaje}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// S-01 · S-09 · S-10 · S-11 · Errores y avisos del sistema
// ─────────────────────────────────────────────────────────────────────────────

/**
 * S-01 · algo no salió como esperábamos.
 *
 * **"Tus datos están a salvo" no es relleno**: es la frase que baja la ansiedad
 * de alguien que acaba de cargar información de salud y ve una pantalla rota.
 * Va con `destructive` y no con el rojo del semáforo — un error de red no es un
 * estado clínico (P-1).
 */
export function ErrorDeCarga({
  titulo = SISTEMA.error.titulo,
  detalle = SISTEMA.error.detalle,
  onReintentar,
}: {
  titulo?: string;
  detalle?: string;
  onReintentar?: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-lg bg-destructive-bg p-6" role="alert">
      <h2 className="text-xl font-bold text-destructive-fg">{titulo}</h2>
      <p className="text-md leading-relax text-destructive-fg">{detalle}</p>
      {onReintentar ? (
        <Boton onClick={onReintentar} variante="secundario">
          {SISTEMA.error.reintentar}
        </Boton>
      ) : null}
    </div>
  );
}

/**
 * S-09 · la sesión se cerró (por inactividad, en el equipo).
 *
 * En una app clínica esto **es una característica de seguridad, no un error**, y
 * el lenguaje lo refleja: se explica el porqué y se asegura que la información
 * quedó guardada. Teal y no rojo.
 *
 * **El reingreso existe desde E3.24, y es distinto por rol** (H-10). El equipo
 * vuelve a poner su contraseña: el botón lo lleva a `/entrar`. El paciente y el
 * cuidador llegan acá sólo si la renovación con su dispositivo ya falló, y lo
 * que les queda es pedir un enlace nuevo, que el equipo ahora sí puede generar
 * (A04). El botón es un enlace de verdad a `/entrar`: recargar la app desde cero
 * es justo lo que hace falta después de una sesión cortada.
 */
export function SesionExpirada({ entraConContrasena }: { entraConContrasena: boolean }) {
  // Un texto por rol (D-314): al equipo se le cortó por inactividad; al paciente
  // y al cuidador, el teléfono dejó de reconocerlos.
  const texto = entraConContrasena
    ? SISTEMA.sesionExpirada.equipo
    : SISTEMA.sesionExpirada.paciente;
  return (
    <div className="flex flex-col gap-4 rounded-lg bg-accent-soft p-6" role="alert">
      <h2 className="text-xl font-bold text-accent-strong">{texto.titulo}</h2>
      <p className="text-md leading-relax text-accent-strong">{texto.detalle}</p>
      <p className="text-md leading-relax text-accent-strong">
        {entraConContrasena
          ? SISTEMA.sesionExpirada.comoVolverEquipo
          : SISTEMA.sesionExpirada.comoVolverPaciente}
      </p>
      {entraConContrasena ? (
        <a
          className="inline-flex min-h-11.5 items-center justify-center self-start rounded-md bg-brand px-6 text-md font-bold text-primary-foreground hover:bg-brand-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          href="/entrar"
        >
          {SISTEMA.sesionExpirada.volverAEntrar}
        </a>
      ) : null}
    </div>
  );
}

/**
 * S-08 · el servidor no contesta, y la persona **sí** tiene sesión (P-99).
 *
 * Es la pantalla que faltaba. Sin conexión, el guard de rol mandaba a la puerta
 * de entrada —"Para entrar necesitás tu enlace"— a un paciente con sesión válida,
 * sobre un enlace que ya estaba gastado: le decía que el problema era su acceso
 * cuando el problema era la señal. Ahora dice lo que pasa, no redirige a ningún
 * lado, y vuelve sola cuando hay conexión.
 *
 * **Amarillo y no rojo, y sin botón:** no hay nada roto ni nada que la persona
 * tenga que arreglar. Lo respondido está guardado y sale solo (R-37).
 */
export function SinConexion() {
  return (
    <div className="flex flex-col gap-4 rounded-lg bg-sem-yellow-bg p-6" role="status">
      <div className="flex items-center gap-3">
        <WifiOff aria-hidden="true" className={`${ICONO.normal} shrink-0 text-sem-yellow-fg`} />
        <h2 className="m-0 text-xl font-bold text-sem-yellow-fg">{SISTEMA.sinConexion.titulo}</h2>
      </div>
      <p className="m-0 text-md leading-relax text-sem-yellow-fg">{SISTEMA.sinConexion.detalle}</p>
      <p className="m-0 text-md leading-relax text-sem-yellow-fg">{SISTEMA.sinConexion.queHacer}</p>
    </div>
  );
}

/**
 * S-10 · esta sección no es parte de tu rol.
 *
 * **No dice "no autorizado"**: dice qué rol tenés y por qué no ves esto, y da
 * una salida concreta. Es lo que ve alguien que llegó a una URL que no le
 * corresponde — el guard del router la usa en vez de un 404, que dejaría a la
 * persona pensando que el enlace está roto.
 */
export function SinPermiso({ rol, onVolver }: { rol?: string; onVolver?: () => void }) {
  return (
    <div className="flex flex-col gap-4 rounded-lg bg-muted p-6" role="alert">
      <h2 className="text-xl font-bold text-heading">{SISTEMA.sinPermiso.titulo}</h2>
      <p className="text-md leading-relax text-muted-foreground">
        {rol ? SISTEMA.sinPermiso.detalleConRol(rol) : SISTEMA.sinPermiso.detalleSinRol}
      </p>
      {onVolver ? (
        <Boton onClick={onVolver} variante="secundario">
          {SISTEMA.sinPermiso.volver}
        </Boton>
      ) : null}
    </div>
  );
}

/** S-11 · mantenimiento programado. Informativo, sin bloquear nada. */
export function AvisoDeMantenimiento({ detalle }: { detalle: string }) {
  return (
    <div className="rounded-lg bg-accent-soft px-4 py-3" role="status">
      <p className="text-md font-semi text-accent-strong">{SISTEMA.mantenimiento.titulo}</p>
      <p className="text-md leading-relax text-accent-strong">{detalle}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// S-07 · S-12 · Vacíos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * S-07 y S-12 · el vacío contextual.
 *
 * Cada vacío explica **por qué está vacío** y **qué tiene que pasar** para que
 * se llene. La carpeta gris genérica no dice ninguna de las dos cosas y deja a
 * la persona sin saber si el sistema falló o si todavía no hay nada.
 */
export function Vacio({
  titulo,
  detalle,
  accion,
}: {
  titulo: string;
  detalle: ReactNode;
  accion?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border-2 border-dashed border-border p-6">
      <h2 className="text-lg font-semi text-heading">{titulo}</h2>
      <div className="text-md leading-relax text-muted-foreground">{detalle}</div>
      {accion}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// S-02 · Éxito
// ─────────────────────────────────────────────────────────────────────────────

/**
 * S-02 · una acción que se completó.
 *
 * **Acotado, no celebratorio.** Un check chico, el mensaje personalizado y el
 * resumen de lo que se confirmó — la transparencia es lo que hace que la persona
 * confíe en que llegó. Los CTA de salida son parte del patrón: un success sin
 * salida deja a la persona sin saber qué hacer.
 */
export function Exito({
  titulo,
  detalle,
  resumen,
  children,
}: {
  titulo: string;
  detalle: string;
  resumen?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5" role="status">
      <span className="flex size-14 items-center justify-center rounded-full bg-sem-green-bg text-sem-green-fg">
        <Check className={ICONO.destacado} />
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="font-serif text-2xl text-heading">{titulo}</h1>
        <p className="text-lg leading-relax text-muted-foreground">{detalle}</p>
      </div>
      {resumen ? (
        <div className="rounded-lg bg-muted p-4 text-md leading-relax text-heading">{resumen}</div>
      ) : null}
      {children}
    </div>
  );
}
